import { computed, ref, watch, getCurrentInstance,nextTick } from "@vue/composition-api";
import { useChatbot } from "@/composables/chatbot";
import { useChatbotFake } from "@/composables/chatbotFake";
import store from "@/store";
import { useToast } from "vue-toastification/composition";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import axios from "@/libs/axios";
import { debounce } from "lodash";
import { useTranslates } from "@/utils/useTranslates";

export function useVideoTour() {
  const toast = useToast();
  const videoTourName = ref("video tour");
  const videoTourInterval = ref(10);
  const userViews = ref([]);
  const userViewsTotalRecords = ref(0);
  const viewsSelected = ref([]);
  const currentPreviewSelected = ref(null);
  const loadingSaveVideoTour = ref(false);
  const { t } = useI18nUtils();
  const instance = getCurrentInstance();
  const viewsPerPage = ref(4);
  const viewsCurrentPage = ref(1);
  const viewsSearch = ref("");
  const loadingUserViews = ref(false);
  const currentTenantSelected = ref(null);
  const vidoTourModalIsOpen = ref(false);
  const videoTourPerPage = ref(4);
  const videoTourCurrentPage = ref(1);
  const videoTourSearch = ref("");
  const loadingVideoTours = ref(false);
  const videoTours = ref([]);
  const videoToursTotalRecords = ref(0);
  const isOpenEdit = ref(false);
  const currentItemToEdit = ref(null);
  const isLoadingSelectedViews = ref(false);
  const isEdit = ref(false);
  const loadingSendToValidation = ref(false);
  const showSendToValidationButton = ref(false);
  const idVideoTour = ref(null);
  const videoToursValidation = ref(null);

  const showSubmitButton = computed(() => {
    if (!isEdit.value && showSendToValidationButton.value) {
      return false;
    } else if (isEdit.value && (videoToursValidation.value) &&  (videoToursValidation.value?.status === 'pending')) {
      return false
    } else {
      return true;
    }
  });

  const tenantOptions = computed(() => store.getters["grid/getAllTenants"]);

  const showAlert = computed(() => (videoToursValidation.value !== null && videoToursValidation.value?.status === 'pending'));

  const translates = {
    "video_tour.success": t("video_tour.success"),
    "video_tour.confirm_delete": t("video_tour.confirm_delete"),
    "video_tour.success_delete": t("video_tour.success_delete"),
    "actions.cancel": t("actions.cancel"),
    "actions.delete": t("actions.delete"),
  };

  function addView(view) {
    const existsView = viewsSelected.value.some(item => item.id === view.id)
    if (existsView) return
    const currentPosition = viewsSelected.value.length + 1
    viewsSelected.value.push({ ...view, position: currentPosition })
  }

  function removeView(idToRemove) {
    viewsSelected.value = viewsSelected.value.filter(item => item.id !== idToRemove)
    viewsSelected.value.forEach((v, i) => v.position = i + 1);
  }

  function addPreview(view) {
    currentPreviewSelected.value = view
  }

  function restartVideoTour() {
    currentPreviewSelected.value = null
    viewsSelected.value = []
    videoTourInterval.value = 10
    currentTenantSelected.value = null
    videoTourName.value = "Video Tour"
    currentItemToEdit.value = null
    viewsPerPage.value = 4;
    viewsCurrentPage.value = 1;
    viewsSearch.value = "";
    isEdit.value = false;
  }

  function onClose() {
    showSendToValidationButton.value = false;
    restartVideoTour()
    instance.proxy.$root.$bvModal.hide("videoTourModal");
    setTimeout(() => {
      vidoTourModalIsOpen.value = false
    }, 100);
  }

  function moveView(id, direction) {
    const views = [...viewsSelected.value];
    const index = views.findIndex(v => v.id === id);
    if (index === -1) return;
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= views.length) return;
    [views[index], views[newIndex]] = [views[newIndex], views[index]];
    views.forEach((v, i) => v.position = i + 1);
    viewsSelected.value = views
  }

  async function onSubmit() {
    if (!viewsSelected.value.length || loadingSaveVideoTour.value) return;
    try {
      loadingSaveVideoTour.value = true;
      const config = viewsSelected.value.map(item => ({
        id: item.id,
        position: item.position
      }));
      const payload = {
        name: videoTourName.value,
        interval_secs: videoTourInterval.value,
        config,
        id_tenant: currentTenantSelected.value.id
      }
      if (isEdit.value) {
        payload.id = currentItemToEdit.value.id
        const { data } = await axios.put(`v1/video-tours/${currentItemToEdit.value.id}`, payload);
      } else {
        const { data } = await axios.post("v1/video-tours", payload);

        idVideoTour.value = data.data.id;
      }

      const messageToast = translates["video_tour.success"]
      contentToast(messageToast, "success")
      loadingSaveVideoTour.value = false;
      showSendToValidationButton.value = true;
      store.commit("grid/toggleReloadinVideoTourWidget");
      getVideoTours()
    } catch (error) {
      loadingSaveVideoTour.value = false;
      contentToast("Error on save video tour", "danger")
      console.log(error)
    }
  }

  function contentToast(message, type) {
    toast({
      component: ToastificationContent,
      props: {
        title: message,
        icon: "CheckIcon",
        variant: type,
      },
    });
  }

  async function loadDataOnEdit(tour) {
    isEdit.value = true;
    loadingSaveVideoTour.value = true;
    isLoadingSelectedViews.value = true;
    setValuesToEdit(tour)
    const { data } = await axios.get(`v1/video-tours/${tour.id}?withViews=true`);
    loadingSaveVideoTour.value = false;
    isLoadingSelectedViews.value = false;
    setValuesToEdit(data.data)
    currentItemToEdit.value = data.data
    setViewToEdit(data.data)
  }

  function setViewToEdit(tour) {
    const config = (tour.video_tours_validation) ? JSON.parse(tour.video_tours_validation.config) : tour.config;
    const posMap = Object.fromEntries(config.map(p => [p.id, p.position]));
    const result = tour.views
      .filter(view => posMap[view.id] !== undefined)
      .map(view => ({
        ...view,
        position: posMap[view.id]
      }))
      .sort((a, b) => a.position - b.position);
    viewsSelected.value = result;

  }

  function setValuesToEdit(tour) {
    videoTourName.value = tour.name
    videoTourInterval.value = tour.interval_secs
    currentTenantSelected.value = tour.tenant
    videoToursValidation.value = tour.video_tours_validation;
    idVideoTour.value = tour.id;
    showSendToValidationButton.value = (!tour.video_tours_validation);
  }

  async function getUserViews() {
    loadingUserViews.value = true
    const params = {
      q: viewsSearch.value,
      max: viewsPerPage.value,
      "page[n]": viewsCurrentPage.value
    }
    const { data } = await axios.get("v1/user-views", { params });
    if (!data.success) return;
    userViews.value = data.data
    userViewsTotalRecords.value = data.meta.total;
    loadingUserViews.value = false
  }

  function updateViewsCurrentPage(page) {
    viewsCurrentPage.value = page
    getUserViews()
  }

  function updateViewsPerPage(perPage) {
    viewsPerPage.value = perPage
    getUserViews()
  }

  const onTypeSearch = debounce(function (search) {
    viewsSearch.value = search;
    getUserViews()
  }, 800);

  function openVideoTourModal(openOnEdit = false) {
    vidoTourModalIsOpen.value = true

    if (!openOnEdit) {
      isOpenEdit.value = false
      currentItemToEdit.value = null
    }

    nextTick(() => {
      instance.proxy.$root.$bvModal.show("videoTourModal");
    });
  }

  function updateVideoTourPerPage(perPage) {
    videoTourPerPage.value = perPage
    getVideoTours()
  }

  function updateVideoTourCurrentPage(page) {
    videoTourCurrentPage.value = page
    getVideoTours()
  }

  function onSearchVideoTour(val) {
    videoTourSearch.value = val
    if (!val) return
    setTimeout(() => {
      getVideoTours()
    }, 100)
  }

  function resetVideoTourSearchBar() {
    videoTourSearch.value = ""
    videoTourCurrentPage.value = 1
    getVideoTours()
  }

  async function getVideoTours() {
    try {
      loadingVideoTours.value = true
      const params = {
        q: videoTourSearch.value,
        max: videoTourPerPage.value,
        "page[n]": videoTourCurrentPage.value
      }
      const { data } = await axios.get("v1/video-tours", { params });
      if (!data.success) return;
      videoTours.value = data.data
      videoToursTotalRecords.value = data?.meta?.total ?? 0;
      loadingVideoTours.value = false
    } catch {
      loadingVideoTours.value = false
    }
  }

  function openEditVideoTour(tour) {
      isOpenEdit.value = true
      currentItemToEdit.value = tour
      videoToursValidation.value = tour.video_tours_validation;
      openVideoTourModal(true)
  }

  async function deleteVideoTour(tour) {
    try {
      const confirmation = await confirmDeleteVideoTour(tour)
      if (!confirmation.isConfirmed) return;
      await axios.delete(`v1/video-tours/${tour.id}`);
      store.commit("grid/toggleReloadinVideoTourWidget");
      contentToast(translates["video_tour.success_delete"], "success")
      getVideoTours()
    } catch (e) {
      console.log(e)
      contentToast("Error on delete video tour", "danger")
    }
  }

  const confirmDeleteVideoTour = async (tour) => {
    const $swal = instance?.proxy?.$swal;

    return await $swal({
      title: useTranslates(translates["video_tour.confirm_delete"], { tour: tour.name }),
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: translates["actions.cancel"],
      confirmButtonText: translates["actions.delete"],
      customClass: {
        confirmButton: "btn btn-primary text-capitalize",
        cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
      },
      buttonsStyling: false,
    });
  };

  const setVideoTour = async (tour) => {
    try {
      await store.dispatch('grid/setVideoTour', tour);
    } catch (error) {
      console.error('Error setting video tour:', error);
    }
  };

  const sendToValidation = async () => {
      await axios.get(`v1/video-tours/${idVideoTour.value}/send-to-validation`)
        .then(response => {
          const { data } = response;

          toast({
            component: ToastificationContent,
            props: {
              title: data.message,
              icon: "CheckIcon",
              variant: "success",
            },
          });

          instance.proxy.$root.$bvModal.hide("videoTourModal");
          restartVideoTour();
          getVideoTours();
        })
        .catch((error) => {
          const { message = 'error' } = error .response.data;

          toast({
            component: ToastificationContent,
            props: {
              title: message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    }

  return {
    videoTourName,
    videoTourInterval,
    userViews,
    viewsSelected,
    currentPreviewSelected,
    loadingSaveVideoTour,
    viewsPerPage,
    viewsCurrentPage,
    userViewsTotalRecords,
    onTypeSearch,
    tenantOptions,
    currentTenantSelected,
    vidoTourModalIsOpen,
    videoTourPerPage,
    videoTourCurrentPage,
    videoTourSearch,
    videoTours,
    videoToursTotalRecords,
    isOpenEdit,
    currentItemToEdit,
    isLoadingSelectedViews,
    loadingVideoTours,
    addView,
    removeView,
    addPreview,
    onClose,
    moveView,
    onSubmit,
    loadDataOnEdit,
    getUserViews,
    updateViewsCurrentPage,
    updateViewsPerPage,
    openVideoTourModal,
    getVideoTours,
    updateVideoTourPerPage,
    updateVideoTourCurrentPage,
    onSearchVideoTour,
    resetVideoTourSearchBar,
    openEditVideoTour,
    deleteVideoTour,
    setVideoTour,
    showSendToValidationButton,
    loadingSendToValidation,
    sendToValidation,
    showSubmitButton,
    videoToursValidation,
    showAlert
  }
}
